/**
 * TroiaEmailLayout - Layout 2 painéis nativo da plataforma
 * Sidebar (w-96/w-16) + Main area (reading pane)
 * Referência: ChatSidebarLeft + ChatBox
 */

import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import type { ConversationResponse } from '@troia-v3/types';
import type { EmailFolderType } from '../../shared/types/email.types';
import { useEmailConversations, useEmailConversation, useEmailSocket } from '../../shared/hooks';
import { useEmailFolders } from '../../shared/hooks/useEmailFolders';
import { useEmailCompose } from '../../shared/hooks/useEmailCompose';
import { useSocket } from '@/contexts/SocketContext';
import { useChannelsDropdown } from '@/modules/channels/hooks/useChannelsDropdown';
import {
  getEmailMessages,
  archiveEmail,
  starEmail,
  unstarEmail,
  trashEmail,
  spamEmail,
  restoreEmail,
  markEmailAsUnread,
  deleteEmail,
} from '../../shared/services/emailService';
import { TroiaSidebar } from './TroiaSidebar';
import { TroiaReadingPane } from './TroiaReadingPane';
import { TroiaComposeFloat } from './TroiaComposeFloat';

type SidebarTab = 'inbox' | 'sent' | 'drafts' | 'spam' | 'trash' | 'archive';

const FOLDER_TO_TAB: Partial<Record<EmailFolderType, SidebarTab>> = {
  inbox: 'inbox',
  sent: 'sent',
  drafts: 'drafts',
  spam: 'spam',
  trash: 'trash',
  archive: 'archive',
};

interface TroiaEmailLayoutProps {
  conversationId?: string;
  onSelectConversation: (id: string) => void;
  onClearSelection: () => void;
}

export const TroiaEmailLayout: React.FC<TroiaEmailLayoutProps> = ({
  conversationId,
  onSelectConversation,
  onClearSelection,
}) => {
  const [selectedId, setSelectedId] = useState<string | undefined>(conversationId);
  const [searchQuery, setSearchQuery] = useState('');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // Auto-colapso do rail de pastas por largura do container (mockup usa @container).
  const rootRef = useRef<HTMLDivElement>(null);
  const [narrowContainer, setNarrowContainer] = useState(false);
  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 0;
      setNarrowContainer(w > 0 && w < 1160);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const [activeTab, setActiveTab] = useState<SidebarTab>('inbox');
  const [showOnlyStarred, setShowOnlyStarred] = useState(false);

  // Sync selectedId when URL conversationId changes (e.g. direct navigation)
  useEffect(() => {
    setSelectedId(conversationId);
  }, [conversationId]);

  const { activeFolder, navigateToFolder, unreadCount, folderCounts } = useEmailFolders();
  const compose = useEmailCompose();
  const { socket } = useSocket();
  const queryClient = useQueryClient();
  useEmailSocket();

  // Email channels for compose "From" field
  const { data: allChannels } = useChannelsDropdown();
  const emailChannels = useMemo(() =>
    (allChannels || []).filter(ch => ch.identifier?.includes('@') && ch.status === 'active'),
    [allChannels]
  );

  const starredFilter = showOnlyStarred ? { conversationTags: ['starred'] } : undefined;

  const {
    data: conversationsData,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isLoading,
  } = useEmailConversations({
    folder: activeFolder,
    search: searchQuery || undefined,
    additionalFilters: starredFilter,
  });

  const conversations: ConversationResponse[] = useMemo(() => {
    return conversationsData?.pages?.flatMap((page) => page?.items || []) || [];
  }, [conversationsData]);

  const { data: selectedConversation } = useEmailConversation(selectedId);

  // Email action mutations
  const invalidateEmailQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['email-conversations'], exact: false });
    queryClient.invalidateQueries({ queryKey: ['email-unread-count'] });
    queryClient.invalidateQueries({ queryKey: ['email-folder-count'] });
  }, [queryClient]);

  const optimisticRemoveConversation = useCallback((conversationId: string) => {
    queryClient.setQueriesData<{ pages: Array<{ items: ConversationResponse[]; total: number }> }>(
      { queryKey: ['email-conversations'] },
      (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            items: page.items.filter((c) => c.id !== conversationId),
            total: Math.max(0, page.total - 1),
          })),
        };
      }
    );
  }, [queryClient]);

  const optimisticToggleTag = useCallback((conversationId: string, tag: string, add: boolean) => {
    queryClient.setQueriesData<{ pages: Array<{ items: ConversationResponse[]; total: number }> }>(
      { queryKey: ['email-conversations'] },
      (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            items: page.items.map((c) => {
              if (c.id !== conversationId) return c;
              const currentTags = c.tags || [];
              const newTags = add
                ? [...currentTags, tag]
                : currentTags.filter((t) => t !== tag);
              return { ...c, tags: newTags };
            }),
          })),
        };
      }
    );
    queryClient.setQueryData<ConversationResponse>(
      ['email-conversation', conversationId],
      (old) => {
        if (!old) return old;
        const currentTags = old.tags || [];
        const newTags = add
          ? [...currentTags, tag]
          : currentTags.filter((t) => t !== tag);
        return { ...old, tags: newTags };
      }
    );
  }, [queryClient]);

  const archiveMutation = useMutation({
    mutationFn: archiveEmail,
    onMutate: (id: string) => {
      optimisticRemoveConversation(id);
      setSelectedId(undefined);
      onClearSelection();
    },
    onSettled: invalidateEmailQueries,
  });

  const starMutation = useMutation({
    mutationFn: (id: string) => {
      const conv = conversations.find(c => c.id === id) || selectedConversation;
      return conv?.tags?.includes('starred') ? unstarEmail(id) : starEmail(id);
    },
    onMutate: (id: string) => {
      const conv = conversations.find(c => c.id === id) || selectedConversation;
      const isCurrentlyStarred = conv?.tags?.includes('starred');
      optimisticToggleTag(id, 'starred', !isCurrentlyStarred);
    },
    onSettled: invalidateEmailQueries,
  });

  const trashMutation = useMutation({
    mutationFn: trashEmail,
    onMutate: (id: string) => {
      optimisticRemoveConversation(id);
      setSelectedId(undefined);
      onClearSelection();
    },
    onSettled: invalidateEmailQueries,
  });

  /**
   * Contato da conversa, resolvido no PONTO DE CHAMADA.
   *
   * Não dá para resolver dentro do `mutationFn`: o `onMutate` roda ANTES dele e
   * já removeu a conversa do cache da lista (`optimisticRemoveConversation`) e
   * limpou a seleção — o `contactId` chegava sempre `undefined` e o remetente
   * nunca era bloqueado, embora a conversa fosse marcada como spam.
   */
  const resolveContactId = useCallback((id: string): string | undefined => {
    const conv = conversations.find((c) => c.id === id) || selectedConversation;
    return conv?.contactId;
  }, [conversations, selectedConversation]);

  const spamMutation = useMutation({
    mutationFn: ({ id, contactId }: { id: string; contactId?: string }) => spamEmail(id, contactId),
    onMutate: ({ id }: { id: string; contactId?: string }) => {
      optimisticRemoveConversation(id);
      setSelectedId(undefined);
      onClearSelection();
    },
    onSettled: invalidateEmailQueries,
  });

  const restoreMutation = useMutation({
    mutationFn: ({ id, fromTag, contactId }: { id: string; fromTag: string; contactId?: string }) =>
      restoreEmail(id, fromTag, contactId),
    onMutate: ({ id }) => {
      optimisticRemoveConversation(id);
      setSelectedId(undefined);
      onClearSelection();
    },
    onSettled: invalidateEmailQueries,
  });

  const markUnreadMutation = useMutation({
    mutationFn: markEmailAsUnread,
    onMutate: () => {
      setSelectedId(undefined);
      onClearSelection();
    },
    onSettled: invalidateEmailQueries,
  });

  /**
   * Excluir permanentemente (Lixeira). Diferente do `trashMutation`, que só
   * move para a lixeira: aqui a conversa é excluída de verdade e não volta.
   *
   * `removeQueries` do detalhe é obrigatório — as outras mutações só invalidam
   * a LISTA, e sem isto a thread excluída continuaria no cache do painel de
   * leitura, reaparecendo ao navegar de volta pela URL.
   */
  const deleteMutation = useMutation({
    mutationFn: deleteEmail,
    onMutate: (id: string) => {
      optimisticRemoveConversation(id);
      setSelectedId(undefined);
      onClearSelection();
      queryClient.removeQueries({ queryKey: ['email-conversation', id] });
      queryClient.removeQueries({ queryKey: ['email-messages', id] });
    },
    onSettled: invalidateEmailQueries,
  });

  // Mark email as read when selected (same as chat read_receipt)
  const markEmailAsRead = useCallback(async (conversationId: string) => {
    if (!socket?.connected) return;

    try {
      const messagesData = await getEmailMessages(conversationId, 1, 50);
      const messageIds = messagesData?.items?.map((m) => m.id) || [];
      if (messageIds.length === 0) return;

      socket.emit('read_receipt', { conversationId, messageIds });

      // Invalidate caches to reflect read state
      queryClient.invalidateQueries({ queryKey: ['email-conversations'] });
      queryClient.invalidateQueries({ queryKey: ['email-unread-count'] });
    } catch {
      // Non-critical — badge will update on next poll
    }
  }, [socket, queryClient]);

  // Trigger mark-as-read when selecting an email
  useEffect(() => {
    if (selectedId) {
      markEmailAsRead(selectedId);
    }
  }, [selectedId, markEmailAsRead]);

  const handleSelect = (id: string) => {
    setSelectedId(id);
    onSelectConversation(id);
  };

  const handleBack = () => {
    setSelectedId(undefined);
    onClearSelection();
  };

  const handleTabChange = (tab: SidebarTab) => {
    setActiveTab(tab);
    setSelectedId(undefined);
    onClearSelection();
    setShowOnlyStarred(false);
  };

  const handleNavigateFolder = (folder: EmailFolderType) => {
    navigateToFolder(folder);
    const tab = FOLDER_TO_TAB[folder];
    if (tab) setActiveTab(tab);
    setSelectedId(undefined);
    onClearSelection();
  };

  const handleReply = () => {
    if (!selectedConversation) return;
    compose.openCompose({
      to: [selectedConversation.contact?.name || ''],
      subject: `Re: ${selectedConversation.subject || ''}`,
      replyToConversationId: selectedConversation.id,
      // Reaproveita o canal da conversa. Sem isto o botão Enviar do popup nasce
      // desabilitado (guard em useEmailCompose.send + TroiaComposeFloat) e o
      // usuário teria que reescolher o "De" a cada resposta.
      ...(selectedConversation.channelId ? { channelId: selectedConversation.channelId } : {}),
    });
  };

  const handleForward = () => {
    if (!selectedConversation) return;
    compose.openCompose({
      subject: `Fwd: ${selectedConversation.subject || ''}`,
      body: `<br/><br/>---------- Mensagem encaminhada ----------<br/>${selectedConversation.lastMessage || ''}`,
    });
  };

  const handleReplyAll = () => {
    if (!selectedConversation) return;
    const metadata = selectedConversation.metadata as Record<string, unknown> | undefined;
    const originalTo = (metadata?.to as string[]) || [];
    const originalCc = (metadata?.cc as string[]) || [];
    const contactEmail = selectedConversation.contact?.name || '';

    compose.openCompose({
      to: [contactEmail, ...originalTo].filter(Boolean),
      cc: originalCc.length > 0 ? originalCc : undefined,
      subject: `Re: ${selectedConversation.subject || ''}`,
      replyToConversationId: selectedConversation.id,
    });
  };

  return (
    <div ref={rootRef} className="relative flex h-full overflow-hidden" style={{ backgroundColor: 'var(--theme-page-background)' }}>
      {/* Sidebar (mobile: lista full-screen, mockup email.html `.mob`) */}
      <TroiaSidebar
        activeTab={activeTab}
        onChangeTab={handleTabChange}
        conversations={conversations}
        selectedId={selectedId}
        onSelect={handleSelect}
        collapsed={sidebarCollapsed || narrowContainer}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        disableToggle={narrowContainer}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        onCompose={() => compose.openCompose({
          channelId: emailChannels.length === 1 ? emailChannels[0].id : undefined,
        })}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
        isLoading={isLoading}
        unreadCount={unreadCount}
        folderCounts={folderCounts}
        onNavigateFolder={handleNavigateFolder}
        showOnlyStarred={showOnlyStarred}
        onToggleStarred={() => setShowOnlyStarred(!showOnlyStarred)}
        onRefresh={invalidateEmailQueries}
      />

      {/* Main: Reading Pane — mobile: slide-over em tela cheia.
          Réplica EXATA do ChatLayout.tsx (mesmo problema, mesma solução):

          1. `max-[899px]:fixed` em vez de `absolute` — `absolute` prende o painel
             ao módulo, e no celular ele ficava espremido ENTRE a appbar (54px) e a
             bottom-nav (72px), perdendo ~126px de altura útil.
          2. `max-lg:[background:var(--theme-page-background-color)]` — a var SEM o
             sufixo `-color` carrega um `<image>` no tema escuro padrão, e usada como
             `backgroundColor` ela computa `transparent`: era por isso que a lista
             aparecia ATRAVÉS do painel de leitura, com os textos sobrepostos.
             Usa `background` (não `backgroundColor`) e a variante sólida. */}
      <div
        data-testid="email-reading-pane-slideover"
        className={`flex flex-col flex-1 min-w-0 min-h-0 inset-0 transition-transform duration-300 ease-out max-[899px]:fixed max-[899px]:z-[55] min-[900px]:absolute min-[900px]:z-30 ${
          selectedId ? 'translate-x-0' : 'translate-x-full'
        } lg:static lg:z-auto lg:flex-1 lg:translate-x-0 lg:transition-none max-lg:[background:var(--theme-page-background-color)] lg:[background-color:var(--theme-page-background)]`}
      >
        <TroiaReadingPane
          conversation={selectedConversation || null}
          activeTab={activeTab}
          onBack={handleBack}
          onReply={handleReply}
          onReplyAll={handleReplyAll}
          onForward={handleForward}
          onArchive={() => selectedId && archiveMutation.mutate(selectedId)}
          onUnarchive={() => selectedId && restoreMutation.mutate({ id: selectedId, fromTag: 'archived' })}
          onStar={() => selectedId && starMutation.mutate(selectedId)}
          onDelete={() => selectedId && trashMutation.mutate(selectedId)}
          onPermanentDelete={() => selectedId && deleteMutation.mutate(selectedId)}
          onMarkSpam={() => selectedId && spamMutation.mutate({ id: selectedId, contactId: resolveContactId(selectedId) })}
          onUnspam={() => selectedId && restoreMutation.mutate({ id: selectedId, fromTag: 'spam', contactId: resolveContactId(selectedId) })}
          onRestore={() => selectedId && restoreMutation.mutate({ id: selectedId, fromTag: 'trash' })}
          onMarkUnread={() => selectedId && markUnreadMutation.mutate(selectedId)}
        />
      </div>

      {/* Compose Float */}
      <TroiaComposeFloat
        state={compose.composeState}
        data={compose.composeData}
        showCc={compose.showCc}
        showBcc={compose.showBcc}
        onSetShowCc={compose.setShowCc}
        onSetShowBcc={compose.setShowBcc}
        onClose={compose.closeCompose}
        onMinimize={compose.minimizeCompose}
        onMaximize={compose.maximizeCompose}
        onRestore={compose.restoreCompose}
        onUpdateField={compose.updateField}
        onSend={compose.send}
        isSending={compose.isSending}
        emailChannels={emailChannels.map(ch => ({ id: ch.id, name: ch.name, identifier: ch.identifier }))}
        uploadedAttachments={compose.uploadedAttachments}
        isUploading={compose.isUploading}
        onRemoveAttachment={compose.removeAttachment}
        onImageUpload={compose.handleFileUpload}
        onFileAttach={compose.handleFileUpload}
        hasContent={compose.hasContent}
      />
    </div>
  );
};

TroiaEmailLayout.displayName = 'TroiaEmailLayout';
